/**
 * src/features/admin/ui/MetricsGrid.tsx
 *
 * Presentational stat-card grid for the AdminMetricsPage (ADMIN-01).
 * Composes CountGrid, MrrGrid, and FunnelRow sub-components.
 *
 * Display only — no hover states, no charts (D-04).
 * All labels via t('admin.*') — no hardcoded strings.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminMetrics } from '@/shared/types/admin';
import { CountGrid } from './CountGrid';
import { MrrGrid } from './MrrGrid';
import { FunnelRow } from './FunnelRow';
import { ActivationRateGrid } from './ActivationRateGrid';
import { TimeToExportCards } from './TimeToExportCards';
import { CohortTable } from './CohortTable';

export interface MetricsGridProps {
  metrics: AdminMetrics;
}

// Re-export AdminMetrics so existing consumers (AdminMetricsPage, index.ts) keep working.
export type { AdminMetrics };

// ---------------------------------------------------------------------------
// MetricsGrid — public component
// ---------------------------------------------------------------------------

export function MetricsGrid({ metrics }: MetricsGridProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      {/* Count grid */}
      <CountGrid metrics={metrics} />

      {/* MRR section */}
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-4">
          {t('admin.section.mrr', 'Monthly Recurring Revenue')}
        </p>
        <MrrGrid mrr={metrics.mrr} />
      </div>

      {/* Funnel section */}
      <FunnelRow funnel={metrics.funnel} />

      {/* Activation section — only when server returns activation data */}
      {metrics.activation && (
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-4">
            {t('admin.section.activation', 'Activation Metrics')}
          </p>
          <ActivationRateGrid cohorts={metrics.activation.cohorts} />

          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mt-6 mb-4">
            {t('admin.section.time_to_export', 'Time to First Export')}
          </p>
          <TimeToExportCards
            p50={metrics.activation.p50MinutesToExport}
            p75={metrics.activation.p75MinutesToExport}
          />

          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mt-6 mb-4">
            {t('admin.section.cohort_table', 'Trial → Paid Cohort Table')}
          </p>
          <CohortTable cohorts={metrics.activation.cohorts} />
        </div>
      )}
    </div>
  );
}
